<template>
  <b-row class="m-3">
    <b-col md="8" offset-md="2" lg="6" offset-lg="3">
      <!-- Success state -->
      <b-card v-if="submitted" header="Message Sent" header-bg-variant="success" header-text-variant="white" bg-variant="light">
        <p class="mb-3">Message sent.</p>
        <div class="text-right">
          <b-button size="sm" variant="primary" :to="{ name: 'login' }">Back to Login</b-button>
        </div>
      </b-card>

      <!-- Step 1: Are you a UBC Member? -->
      <b-card v-else-if="isMember === null" header="Contact Us" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <p class="mb-4">Are you a UBC Member?</p>
        <div class="d-flex justify-content-start">
          <b-button variant="success" class="mr-3" @click="isMember = true">Yes</b-button>
          <b-button variant="secondary" @click="isMember = false">No</b-button>
        </div>
      </b-card>

      <!-- Non-member: redirect info -->
      <b-card v-else-if="isMember === false" header="Contact UBC" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <p class="mb-3">
          For information on how to join, please visit the
          <a href="https://www.carpenters.org/join-us/" target="_blank" rel="noopener">Join Us</a>
          page or contact a
          <a href="https://www.carpenters.org/regional-council/" target="_blank" rel="noopener">UBC Regional Council</a>
          in your area to get started.
        </p>
        <p class="mb-3">
          Use the Regional Council directory to locate a Regional Council in your area.
        </p>
        <div class="text-right">
          <b-button size="sm" variant="secondary" @click="isMember = null">Back</b-button>
        </div>
      </b-card>

      <!-- Member contact form -->
      <b-card v-else header="Contact Us" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <p class="mb-2"><strong>Please contact your Council or Local Union directly to:</strong></p>
        <ul class="mb-3">
          <li>Get your UBC ID number</li>
          <li>Pay dues</li>
          <li>Ask questions about your pension, insurance, and other benefits</li>
          <li>Submit general requests for information or grievances</li>
        </ul>
        <p class="mb-3">
          Use the
          <a href="https://www.carpenters.org/regional-council/" target="_blank" rel="noopener">Regional Council directory</a>
          to locate a UBC Regional Council in your area.
        </p>
        <p class="mb-4">
          For other inquiries, please complete the form below. The Carpenters Member Portal site support will respond to your inquiry within 48 hours
          (Monday – Friday).
        </p>
        <b-form @submit.prevent="onSubmit">
          <b-form-group label="UBC ID:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.ubc_id" v-mask="'U-####-####'" :state="validated('ubc_id')" placeholder="e.g. U-1234-5678" trim @paste="onUbcIdPaste" />
            <b-form-invalid-feedback>{{ ubcIdError }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Local Union:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.local_union" :state="validated('local_union')" placeholder="e.g. Local 123" maxlength="6" trim />
            <b-form-invalid-feedback>Local Union is required</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Email:" label-cols-sm="4" label-align-sm="right">
            <b-input type="email" v-model="form.email" :state="validated('email')" placeholder="Email Address" trim />
            <b-form-invalid-feedback>{{ emailError }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Subject:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.subject" :state="validated('subject')" placeholder="Subject" maxlength="100" trim />
            <b-form-invalid-feedback>Subject is required</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Message:" label-cols-sm="4" label-align-sm="right">
            <b-textarea v-model="form.message" :state="validated('message')" placeholder="Your message..." rows="5" maxlength="500" trim />
            <b-form-invalid-feedback>Message is required</b-form-invalid-feedback>
          </b-form-group>

          <div class="d-flex justify-content-between align-items-center mt-3">
            <b-button size="sm" variant="secondary" :disabled="loading" @click="isMember = null">Back</b-button>
            <b-button size="sm" type="submit" variant="success" :disabled="loading">
              <b-spinner v-if="loading" small class="mr-1" />
              Send Message
            </b-button>
          </div>
        </b-form>
      </b-card>

      <recaptcha-v2-modal
        v-model="showV2Challenge"
        :site-key="recaptchaV2SiteKey"
        @resolved="onV2Resolved"
        @cancelled="onV2Cancelled"
        @error="onV2Error"
      />
    </b-col>
  </b-row>
</template>
<script>
import { Vue, Component, Mixins, Watch } from 'vue-property-decorator';
import memberDao from '@/dao/member_dao';
import { emailValidator } from '@/util/validator';
import { isValid as isValidUbcId } from '@/util/ubc_id';
import RecaptchaV2Modal from '@/components/RecaptchaV2Modal.vue';
import recaptchaMixin from '@/mixins/recaptcha_fallback';
import { renderErrorBody } from '@/util/error';

@Component({
  metaInfo: {
    title: 'Member Portal - Contact Us'
  },
  components: { RecaptchaV2Modal }
})
export default class ContactUs extends Mixins(recaptchaMixin) {
  isMember = null;
  submitted = false;
  formSubmitted = false;
  loading = false;

  form = {
    ubc_id: '',
    local_union: '',
    email: '',
    subject: '',
    message: ''
  };

  onUbcIdPaste(e) {
    e.preventDefault();
    const raw = (e.clipboardData || window.clipboardData).getData('text');
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let val = 'U';
    if (digits.length > 0) val += '-' + digits.slice(0, 4);
    if (digits.length > 4) val += '-' + digits.slice(4);
    this.form.ubc_id = val;
  }

  get ubcIdError() {
    return 'The UBC ID number provided is invalid. Please enter a valid UBC ID.';
  }

  get emailError() {
    return 'The e-mail address provided is invalid. Please enter a valid email address.';
  }

  validated(field) {
    if (!this.formSubmitted && !this.form[field]) return undefined;
    const value = (this.form[field] || '').trim();
    switch (field) {
      case 'ubc_id':
        return isValidUbcId(value);
      case 'email':
        return emailValidator(value);
      case 'local_union':
      case 'subject':
      case 'message':
        return value.length > 0;
      default:
        return true;
    }
  }

  get formValid() {
    return ['ubc_id', 'local_union', 'email', 'subject', 'message'].every(k => this.validated(k) === true);
  }

  async onSubmit() {
    this.formSubmitted = true;

    if (!this.formValid) {
      return this.$bvToast.toast('Please correct the highlighted errors.', {
        title: 'Error',
        solid: true,
        variant: 'danger'
      });
    }

    try {
      this.loading = true;

      await this.submitWithRecaptcha('contact', tokens =>
        memberDao.submitContactForm({
          ubc_id: this.form.ubc_id.trim(),
          local_union: this.form.local_union.trim(),
          email: this.form.email.trim(),
          subject: this.form.subject.trim(),
          message: this.form.message.trim(),
          ...tokens
        })
      );
      this.submitted = true;
    } catch (error) {
      const body = renderErrorBody(this.$createElement, (error && error.message) || 'An unknown error occurred, please try again.');
      this.$bvModal.msgBoxOk(body, {
        title: 'Unable to Send Message',
        okTitle: 'OK',
        okVariant: 'danger',
        centered: true,
        headerBgVariant: 'danger',
        headerTextVariant: 'white'
      });
    } finally {
      this.loading = false;
    }
  }

  @Watch('isMember')
  onIsMemberChange(val) {
    if (val === true) {
      this.loadRecaptcha();
    }
  }
}
</script>
<style scoped></style>
